import React from "react";
import { Sheet, Chip, Tooltip, Box, Option, Button } from "@mui/joy";
import DarkInput from "../../common/DarkInput";
import type {
  EquipmentAttributeDefinition,
  EquipmentModel,
  EquipmentPrefilter,
  PrefilterCriteria,
} from "../../../api/equipment/Equipment.typed";
import { AddIcon, PlaylistAddIcon, SEdit, SDelete, SDone, SClose } from "../../../styles/StyledMUI";
import { usePermissions, Permission } from "../../../contexts/app/permissions";
import { getOperatorLabel } from "../../../utils/prefilterHelpers";
import CriteriaBuilder from "./common/CriteriaBuilder";
import DarkSelect from "../../common/DarkSelect";
import IconButtonCTA from "../../common/IconButtonCTA";
import { colorTokens, withAlpha } from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";

interface EquipmentPrefiltersListProps {
  model: EquipmentModel;
  onModelChange: (model: EquipmentModel) => void;
}

export default function EquipmentPrefiltersList({
  model,
  onModelChange,
}: EquipmentPrefiltersListProps) {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.EQUIPMENT_EDIT);
  const [editingFilterIdx, setEditingFilterIdx] = React.useState<number | null>(null);
  const [editingFilter, setEditingFilter] = React.useState<EquipmentPrefilter | null>(null);
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);

  const removePrefilter = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = {
      ...model,
      Prefilters: (model.Prefilters || []).filter(
        (_: EquipmentPrefilter, i: number) => i !== index,
      ),
    };
    onModelChange(updated);
  };

  const startEditPrefilter = (idx: number) => {
    const filter = model.Prefilters?.[idx];
    if (filter) {
      setEditingFilterIdx(idx);
      setEditingFilter({ ...filter });
    }
  };

  const addEmptyPrefilter = () => {
    const empty: EquipmentPrefilter = {
      ID: crypto.randomUUID(),
      Name: "",
      Criteria: [{ AttributeID: "", Operator: "=", Value: "" }],
      Logic: "AND",
    };
    const next = { ...model };
    next.Prefilters = [empty, ...(next.Prefilters || [])];
    onModelChange(next);
    setEditingFilterIdx(0);
    setEditingFilter({ ...empty });
  };

  const savePrefilter = () => {
    if (editingFilterIdx !== null && editingFilter) {
      const updated = {
        ...model,
        Prefilters: (model.Prefilters || []).map((f: EquipmentPrefilter, i: number) =>
          i === editingFilterIdx ? editingFilter : f,
        ),
      };
      onModelChange(updated);
      setEditingFilterIdx(null);
      setEditingFilter(null);
    }
  };

  const cancelEditPrefilter = () => {
    if (editingFilterIdx !== null) {
      const pf = model.Prefilters?.[editingFilterIdx];
      const isNewAndEmpty =
        pf &&
        (!pf.Name || pf.Name.trim() === "") &&
        (!pf.Criteria || pf.Criteria.length === 0 || (pf.Criteria[0].AttributeID || "") === "");
      if (isNewAndEmpty) {
        const updated = {
          ...model,
          Prefilters: (model.Prefilters || []).filter(
            (_: EquipmentPrefilter, i: number) => i !== editingFilterIdx,
          ),
        };
        onModelChange(updated);
      }
    }
    setEditingFilterIdx(null);
    setEditingFilter(null);
  };

  const addCriteriaToEditingFilter = () => {
    if (!editingFilter) return;
    setEditingFilter((prev: EquipmentPrefilter | null) =>
      prev
        ? {
            ...prev,
            Criteria: (prev.Criteria ? [...prev.Criteria] : []).concat([
              { AttributeID: "", Operator: "=", Value: "" },
            ]),
          }
        : null,
    );
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", "");
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null) return;

    if (draggedIndex !== dropIndex) {
      const newPrefilters = [...(model.Prefilters || [])];
      const [movedItem] = newPrefilters.splice(draggedIndex, 1);
      newPrefilters.splice(dropIndex, 0, movedItem);
      onModelChange({ ...model, Prefilters: newPrefilters });
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const Header = (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
      <DarkTypography level="body-xs" colorVariant="muted">
        Filtres configurés
      </DarkTypography>
      {canManage && (
        <Button
          variant="plain"
          size="sm"
          onClick={addEmptyPrefilter}
          startDecorator={<PlaylistAddIcon />}
          sx={{
            color: colorTokens.status.success,
            "&:hover": { backgroundColor: withAlpha(colorTokens.status.success, 0.1) },
          }}
        >
          Ajouter un filtre
        </Button>
      )}
    </Box>
  );

  return (
    <>
      {Header}
      {(!model.Prefilters || model.Prefilters.length === 0) && (
        <DarkTypography level="body-sm" colorVariant="muted" sx={{ fontStyle: "italic", mb: 1 }}>
          Aucun filtre configuré
        </DarkTypography>
      )}
      {(model.Prefilters || []).map((prefilter: EquipmentPrefilter, idx: number) => {
        const isEditing = editingFilterIdx === idx;

        return (
          <Sheet
            key={prefilter.ID}
            variant="soft"
            draggable={!isEditing}
            onDragStart={(e) => handleDragStart(e, idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, idx)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              borderRadius: 3,
              backgroundColor: colorTokens.bg.surface,
              border: `1px solid ${colorTokens.border.default}`,
              cursor: isEditing ? "default" : "grab",
              opacity: draggedIndex === idx ? 0.5 : 1,
              transform: dragOverIndex === idx && draggedIndex !== idx ? "scale(1.02)" : "scale(1)",
              transition: "all 0.15s ease",
              borderColor:
                dragOverIndex === idx && draggedIndex !== idx
                  ? colorTokens.accent.primary
                  : colorTokens.border.default,
              "&:hover": {
                borderColor: isEditing ? colorTokens.border.default : colorTokens.border.strong,
              },
              "&:active": {
                cursor: isEditing ? "default" : "grabbing",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!isEditing && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mr: 1,
                    color: colorTokens.text.muted,
                    cursor: "grab",
                    "&:hover": {
                      color: colorTokens.accent.primary,
                    },
                  }}
                >
                  <Tooltip title="Glisser pour réorganiser">
                    <Box sx={{ fontSize: "14px", lineHeight: 1 }}>⋮⋮</Box>
                  </Tooltip>
                </Box>
              )}

              {isEditing ? (
                <DarkInput
                  value={editingFilter?.Name || ""}
                  onChange={(e) =>
                    setEditingFilter((prev: EquipmentPrefilter | null) =>
                      prev ? { ...prev, Name: e.target.value } : null,
                    )
                  }
                  placeholder="Nom du filtre"
                  sx={{ flexGrow: 1 }}
                  size="sm"
                />
              ) : (
                <DarkTypography
                  sx={{ flexGrow: 1, fontWeight: "bold", color: colorTokens.accent.primary }}
                >
                  {prefilter.Name}
                </DarkTypography>
              )}

              <Box sx={{ display: "flex", gap: 0.5 }}>
                {isEditing ? (
                  <>
                    <Tooltip title="Sauvegarder">
                      <IconButtonCTA
                        ctaVariant="success"
                        size="sm"
                        aria-label="Sauvegarder filtre"
                        onClick={savePrefilter}
                      >
                        <SDone />
                      </IconButtonCTA>
                    </Tooltip>
                    <Tooltip title="Annuler">
                      <IconButtonCTA
                        ctaVariant="danger"
                        size="sm"
                        aria-label="Annuler édition filtre"
                        onClick={cancelEditPrefilter}
                      >
                        <SClose />
                      </IconButtonCTA>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    {canManage && (
                      <Tooltip title="Modifier le filtre">
                        <IconButtonCTA
                          ctaVariant="neutral"
                          size="sm"
                          aria-label="Modifier filtre"
                          onClick={() => startEditPrefilter(idx)}
                        >
                          <SEdit />
                        </IconButtonCTA>
                      </Tooltip>
                    )}
                    {canManage && (
                      <Tooltip title="Supprimer">
                        <IconButtonCTA
                          ctaVariant="danger"
                          size="sm"
                          aria-label="Supprimer filtre"
                          onClick={(e) => removePrefilter(idx, e)}
                        >
                          <SDelete />
                        </IconButtonCTA>
                      </Tooltip>
                    )}
                  </>
                )}
              </Box>
            </Box>

            {isEditing && editingFilter ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                  >
                    <DarkTypography level="body-xs" colorVariant="muted">
                      Critères de filtrage
                    </DarkTypography>
                    <Button
                      variant="plain"
                      size="sm"
                      onClick={addCriteriaToEditingFilter}
                      sx={{
                        color: colorTokens.status.success,
                        "&:hover": { backgroundColor: withAlpha(colorTokens.status.success, 0.1) },
                      }}
                      startDecorator={<AddIcon />}
                    >
                      Ajouter
                    </Button>
                  </Box>

                  <CriteriaBuilder
                    attributes={model.Attributes || []}
                    criteria={editingFilter.Criteria || []}
                    logic={editingFilter.Logic || "AND"}
                    onChange={(next: { criteria: PrefilterCriteria[]; logic: "AND" | "OR" }) =>
                      setEditingFilter((prev: EquipmentPrefilter | null) =>
                        prev ? { ...prev, Criteria: next.criteria, Logic: next.logic } : null,
                      )
                    }
                  />

                  {(editingFilter.Criteria || []).length > 1 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DarkTypography level="body-xs" colorVariant="muted">
                        Logique de combinaison:
                      </DarkTypography>
                      <DarkSelect
                        value={editingFilter.Logic || "AND"}
                        onChange={(_, value) =>
                          setEditingFilter((prev: EquipmentPrefilter | null) =>
                            prev ? { ...prev, Logic: value as "AND" | "OR" } : null,
                          )
                        }
                        size="sm"
                        sx={{ width: 120 }}
                      >
                        <Option value="AND">ET</Option>
                        <Option value="OR">OU</Option>
                      </DarkSelect>
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {(prefilter.Criteria || []).length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Chip
                      size="sm"
                      variant="soft"
                      sx={{
                        backgroundColor: colorTokens.bg.surfaceMuted,
                        color: colorTokens.accent.primary,
                      }}
                    >
                      {(prefilter.Criteria || []).length} critères
                    </Chip>
                    <Chip
                      size="sm"
                      variant="soft"
                      sx={{
                        backgroundColor: colorTokens.bg.surfaceMuted,
                        color: colorTokens.text.secondary,
                      }}
                    >
                      Logique: {prefilter.Logic || "AND"}
                    </Chip>
                  </Box>
                )}

                {(prefilter.Criteria || []).map(
                  (criteria: PrefilterCriteria, criteriaIdx: number) => {
                    const attr = (model.Attributes || []).find(
                      (a: EquipmentAttributeDefinition) => a.ID === criteria.AttributeID,
                    );
                    return (
                      <Box
                        key={criteriaIdx}
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          flexWrap: "wrap",
                          pl: (prefilter.Criteria || []).length > 1 ? 2 : 0,
                          position: "relative",
                        }}
                      >
                        {(prefilter.Criteria || []).length > 1 && (
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: "50%",
                              transform: "translateY(-50%)",
                              width: 4,
                              height: 4,
                              borderRadius: "50%",
                              backgroundColor: colorTokens.accent.primary,
                            }}
                          />
                        )}
                        <Chip
                          size="sm"
                          variant="soft"
                          sx={{
                            backgroundColor: colorTokens.bg.surfaceMuted,
                            color: colorTokens.accent.primary,
                          }}
                        >
                          {attr?.Label || criteria.AttributeID}
                        </Chip>
                        <Chip
                          size="sm"
                          variant="soft"
                          sx={{
                            backgroundColor: colorTokens.bg.surfaceMuted,
                            color: colorTokens.text.secondary,
                          }}
                        >
                          {getOperatorLabel(criteria.Operator)}
                        </Chip>
                        <Chip
                          size="sm"
                          variant="soft"
                          sx={{
                            backgroundColor: colorTokens.accent.primary,
                            color: colorTokens.accent.contrastText,
                          }}
                        >
                          {criteria.Value}
                        </Chip>
                      </Box>
                    );
                  },
                )}
              </Box>
            )}
          </Sheet>
        );
      })}
    </>
  );
}
